import {
  Button,
  PageTitle,
  SearchPage,
  useModal,
  useToastr,
} from '@farmshare/ui-components';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import {
  Chip,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopy as DuplicateIcon,
  Delete as DeleteIcon,
  KeyboardArrowDown,
  Visibility as PreviewIcon,
} from '@mui/icons-material';
import { useRecoilValue } from 'recoil';
import { vendorState } from 'state';

import { CutsheetResponsePreview } from 'features/customers/components/_tabs/cutsheet-response-preview';
import {
  useCatalogState,
  useQuestionnaireState,
} from 'features/cutsheets/v2/hooks';
import { QuestionnaireWizard } from 'features/cutsheets/v2/components';
import type {
  InitialResponseSeed,
  ResponseTrimSelection,
} from 'features/cutsheets/v2/components/questionnaire-wizard/types';

import {
  type CutsheetQuestionnaireResponsePagination,
  type CutsheetQuestionnaireResponseSearchQuery,
  type CutsheetQuestionnaireResponseSearchQueryVariables,
  EnumCutsheetQuestionnaireResponseType,
  SortFindManyCutsheetQuestionnaireResponseInput,
  useCutsheetCapabilityManyQuery,
  useCutsheetQuestionnaireResponseByIdQuery,
  useCutsheetQuestionnaireResponseSearchLazyQuery,
  useCutsheetQuestionnaireResponseSoftDeleteMutation,
  useCutsheetQuestionnairesByVendorQuery,
} from 'lib/graphql';

type ResponseItem = NonNullable<
  NonNullable<
    CutsheetQuestionnaireResponseSearchQuery['cutsheetQuestionnaireResponseSearch']
  >['items']
>[number];

type QuestionnaireRef =
  | string
  | { _id?: string | null; id?: string | null }
  | null
  | undefined;

function getQuestionnaireId(value: QuestionnaireRef): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  if (value._id) return String(value._id);
  if (value.id) return String(value.id);
  return undefined;
}

export function QuestionnaireResponsesPage() {
  const vendor = useRecoilValue(vendorState);
  const vendorId = vendor?._id;
  const { ask } = useModal();
  const { push } = useToastr();

  const [previewResponseId, setPreviewResponseId] = useState<string | null>(
    null,
  );

  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState<
    string | undefined
  >();
  const [duplicateResponseId, setDuplicateResponseId] = useState<
    string | undefined
  >();
  const [wizardOpen, setWizardOpen] = useState(false);

  const [_loadData, queryResult] =
    useCutsheetQuestionnaireResponseSearchLazyQuery({
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
    });

  const searchLazyQuery: [typeof _loadData, typeof queryResult] = [
    _loadData,
    queryResult,
  ];

  const [softDelete] = useCutsheetQuestionnaireResponseSoftDeleteMutation();

  const { data: questionnairesData } = useCutsheetQuestionnairesByVendorQuery({
    variables: { vendorId: vendorId ?? '' },
    skip: !vendorId,
  });

  const { data: capabilitiesData } = useCutsheetCapabilityManyQuery({
    variables: { vendorId: vendorId ?? '' },
    skip: !vendorId,
  });

  const groupedQuestionnaires = useMemo(() => {
    const questionnaires = (
      questionnairesData?.cutsheetQuestionnairesByVendor ?? []
    ).filter((q): q is NonNullable<typeof q> => !!q);

    const capabilities =
      capabilitiesData?.CutsheetCapabilityByVendorAndAnimal ?? [];
    const capMap = Object.fromEntries(
      capabilities
        .filter((c): c is NonNullable<typeof c> => !!c)
        .map((c) => [String(c._id), c.name]),
    );

    const groups: Record<
      string,
      {
        capabilityId: string;
        label: string;
        questionnaires: typeof questionnaires;
      }
    > = {};

    for (const q of questionnaires) {
      const capId = String((q as any).capabilityId ?? '');
      const label = capMap[capId] ?? 'Other';
      if (!groups[capId]) {
        groups[capId] = { capabilityId: capId, label, questionnaires: [] };
      }
      groups[capId].questionnaires.push(q);
    }

    return Object.values(groups);
  }, [questionnairesData, capabilitiesData]);

  const questionnaireFilterOptions = useMemo(
    () =>
      groupedQuestionnaires.flatMap((group) =>
        group.questionnaires.map((q) => ({
          label:
            group.label && group.label !== 'Other'
              ? `${group.label} — ${q.name}`
              : q.name,
          value: String(q._id),
        })),
      ),
    [groupedQuestionnaires],
  );

  const searchFilters = useMemo(
    () => [
      {
        label: 'Type',
        value: 'type',
        type: 'multi-select' as const,
        selectedValues: [EnumCutsheetQuestionnaireResponseType.Vendor],
        options: [
          {
            label: 'Vendor',
            value: EnumCutsheetQuestionnaireResponseType.Vendor,
          },
          {
            label: 'Custom',
            value: EnumCutsheetQuestionnaireResponseType.User,
          },
        ],
      },
      {
        label: 'Questionnaire',
        value: 'questionnaire',
        type: 'select' as const,
        options: questionnaireFilterOptions,
      },
      {
        label: 'Include Archived',
        value: 'includeDeleted',
        type: 'checkbox' as const,
      },
    ],
    [questionnaireFilterOptions],
  );

  const { data: duplicateResponseData, loading: duplicateResponseLoading } =
    useCutsheetQuestionnaireResponseByIdQuery({
      variables: { _id: duplicateResponseId ?? '' },
      skip: !duplicateResponseId,
      fetchPolicy: 'cache-and-network',
    });

  const duplicateResponse =
    duplicateResponseData?.cutsheetQuestionnaireResponseById;

  const effectiveQuestionnaireId =
    selectedQuestionnaireId ??
    getQuestionnaireId(duplicateResponse?.questionnaire);

  const { questionnaire, loading: qLoading } = useQuestionnaireState({
    questionnaireId: effectiveQuestionnaireId,
  });

  const { catalog, loading: catalogLoading } = useCatalogState({
    vendorId: vendorId ?? '',
    capabilityId: questionnaire?.capabilityId ?? '',
  });

  const initialResponse = useMemo<InitialResponseSeed | undefined>(() => {
    if (!duplicateResponse) return undefined;
    return {
      name: duplicateResponse.name
        ? `${duplicateResponse.name} (Copy)`
        : undefined,
      selectedCuts: (duplicateResponse.selectedCuts ?? [])
        .filter((c): c is NonNullable<typeof c> => !!c)
        .map((c) => ({
          optionId: c.optionId,
          specifications: c.specifications
            ?.filter((s): s is NonNullable<typeof s> => !!s)
            .map((s) => ({
              specification: s.specification,
              type: s.type as 'text' | 'number' | 'select' | 'multiselect',
              value: s.value as string | number | string[],
            })),
        })),
      trimSelections: duplicateResponse.trimSelections
        ? {
            type: duplicateResponse.trimSelections.type as
              | 'ranked'
              | 'selection_by_percentage',
            trim: (duplicateResponse.trimSelections.trim ?? [])
              .filter((t): t is NonNullable<typeof t> => !!t)
              .map((t) => {
                const specifications = t.specifications
                  ?.filter((s): s is NonNullable<typeof s> => !!s)
                  .map((s) => ({
                    specification: s.specification,
                    type: s.type as
                      | 'text'
                      | 'number'
                      | 'select'
                      | 'multiselect',
                    value: s.value as string | number | string[],
                  }));
                if (duplicateResponse.trimSelections!.type === 'ranked') {
                  return {
                    optionId: t.optionId,
                    rank: t.rank ?? 0,
                    quantity: t.quantity ?? 0,
                    ...(specifications ? { specifications } : {}),
                  } satisfies ResponseTrimSelection;
                }
                return {
                  optionId: t.optionId,
                  percentage: t.percentage ?? 0,
                  ...(specifications ? { specifications } : {}),
                } satisfies ResponseTrimSelection;
              }),
          }
        : undefined,
      specifications: (duplicateResponse.specifications ?? [])
        .filter((s): s is NonNullable<typeof s> => !!s)
        .map((s) => ({
          specification: s.specification,
          type: s.type as 'text' | 'number' | 'select' | 'multiselect',
          value: s.value as string | number | string[],
        })),
    };
  }, [duplicateResponse]);

  const isDuplicating = !!duplicateResponseId;
  const isCreating = !!selectedQuestionnaireId;

  const wizardReady =
    (isCreating || isDuplicating) &&
    !qLoading &&
    !catalogLoading &&
    !!questionnaire &&
    !!catalog &&
    (!isDuplicating || !!initialResponse);

  useEffect(() => {
    if (wizardReady && !wizardOpen) {
      setWizardOpen(true);
    }
  }, [wizardReady, wizardOpen]);

  const isLoadingWizard =
    (isCreating || isDuplicating) &&
    !wizardOpen &&
    (qLoading || catalogLoading || duplicateResponseLoading);

  const handleDelete = (row: ResponseItem) => {
    if (!row) return;
    ask({
      type: 'ask',
      title: 'Delete Response',
      body: `Are you sure you want to delete "${row.name}"? This cannot be undone.`,
      onConfirm: async () => {
        await softDelete({ variables: { id: row._id } });
        push({
          title: 'Deleted',
          body: 'Response deleted.',
          bg: 'success',
          delay: 3000,
        });
        queryResult.refetch?.();
      },
    });
  };

  const handleDuplicate = (row: ResponseItem) => {
    if (!row) return;
    setSelectedQuestionnaireId(undefined);
    setDuplicateResponseId(String(row._id));
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setSelectedQuestionnaireId(undefined);
    setDuplicateResponseId(undefined);
  };

  const handleWizardSave = (responseId: string) => {
    queryResult.refetch?.();
    handleWizardClose();
    push({
      title: 'Saved',
      body: 'Questionnaire response saved.',
      bg: 'success',
      delay: 3000,
    });
    void responseId;
  };

  return (
    <Container>
      <PageTitle
        title="Cut Sheets"
        breadCrumbLabel="Cut Sheets"
        subTitle="A list of all cut sheets."
        action={
          <Stack direction="row" alignItems="center" spacing={1}>
            {isLoadingWizard && <CircularProgress size={18} />}
            <Button
              content="Add Cutsheet"
              muiIcon={<KeyboardArrowDown />}
              size="sm"
              disabled={isLoadingWizard || groupedQuestionnaires.length === 0}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                setMenuAnchor(e.currentTarget);
              }}
            />
            <Menu
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={() => setMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{ paper: { sx: { minWidth: 220, maxWidth: 280 } } }}
            >
              {groupedQuestionnaires.map((group, groupIdx) => [
                <ListSubheader
                  key={`header-${group.capabilityId}`}
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    lineHeight: '28px',
                    py: 0.5,
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </ListSubheader>,
                ...group.questionnaires.map((q) => (
                  <MenuItem
                    key={q._id}
                    onClick={() => {
                      setMenuAnchor(null);
                      setDuplicateResponseId(undefined);
                      setSelectedQuestionnaireId(String(q._id));
                    }}
                    sx={{ py: 0.75, px: 3, minHeight: 'auto' }}
                  >
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {q.name}
                    </Typography>
                  </MenuItem>
                )),
                groupIdx < groupedQuestionnaires.length - 1 ? (
                  <Divider
                    key={`divider-${group.capabilityId}`}
                    sx={{ my: 0.5 }}
                  />
                ) : null,
              ])}
            </Menu>
          </Stack>
        }
      />
      <SearchPage<
        ResponseItem,
        CutsheetQuestionnaireResponsePagination,
        CutsheetQuestionnaireResponseSearchQuery,
        CutsheetQuestionnaireResponseSearchQueryVariables,
        SortFindManyCutsheetQuestionnaireResponseInput
      >
        paginationQuery={searchLazyQuery}
        dataAccessor={(data) =>
          data.cutsheetQuestionnaireResponseSearch as CutsheetQuestionnaireResponsePagination
        }
        columns={[
          {
            label: 'Name',
            field: 'name',
            formatter: (row) => (
              <div>
                <Typography variant="body2" fontWeight={500}>
                  {row?.name}
                </Typography>
                {row?.notes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      maxWidth: 240,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.notes}
                  </Typography>
                )}
              </div>
            ),
          },
          {
            label: 'Questionnaire',
            field: 'questionnaireName',
            minimumBreakpoint: 'md',
            formatter: (row) => row?.questionnaireName || '—',
          },
          {
            label: 'Customer',
            field: 'userFullName',
            minimumBreakpoint: 'lg',
            formatter: (row) => row?.userFullName || 'N/A',
          },
          {
            label: 'Date',
            field: 'createdAt',
            minimumBreakpoint: 'md',
            formatter: (row) =>
              row?.createdAt
                ? format(new Date(row.createdAt as string), 'MMM d, yyyy')
                : '—',
          },
          {
            label: 'Type',
            field: 'type',
            formatter: (row) => (
              <Chip
                label={
                  row?.type === EnumCutsheetQuestionnaireResponseType.Vendor
                    ? 'Vendor'
                    : 'Custom'
                }
                size="small"
                color={
                  row?.type === EnumCutsheetQuestionnaireResponseType.Vendor
                    ? 'primary'
                    : 'default'
                }
                variant="outlined"
              />
            ),
          },
          {
            formatter: (row) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Tooltip title="Preview">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => row && setPreviewResponseId(row._id)}
                  >
                    <PreviewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate">
                  <IconButton
                    size="small"
                    disabled={isLoadingWizard}
                    onClick={() => row && handleDuplicate(row)}
                  >
                    <DuplicateIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => row && handleDelete(row)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            ),
          },
        ]}
        sortOptions={[
          {
            label: 'Newest',
            value: SortFindManyCutsheetQuestionnaireResponseInput.CreatedatDesc,
          },
          {
            label: 'Oldest',
            value: SortFindManyCutsheetQuestionnaireResponseInput.CreatedatAsc,
          },
          {
            label: 'Name (A–Z)',
            value: SortFindManyCutsheetQuestionnaireResponseInput.IdAsc,
          },
        ]}
        defaultSort={
          SortFindManyCutsheetQuestionnaireResponseInput.CreatedatDesc
        }
        buildQuery={(searchValue, selectedFilters, sort, page, perPage) => {
          const filterObj: Record<string, any> = {};

          selectedFilters.forEach((filter) => {
            if (!filter.isActive) return;
            if (filter.value === 'type' && filter.selectedValues) {
              filterObj.type = filter.selectedValues;
            }
            if (filter.value === 'questionnaire' && filter.selectedValue) {
              filterObj.questionnaireId = filter.selectedValue;
            }
            if (filter.value === 'includeDeleted') {
              filterObj.includeDeleted = true;
            }
          });

          return {
            vendorId: vendor?._id,
            query: searchValue || undefined,
            ...filterObj,
            sort,
            page,
            perPage,
          };
        }}
        searchPlaceholder="Search responses by name or customer"
        filters={searchFilters}
        persistentFilters
      />
      {previewResponseId && vendor?._id && (
        <CutsheetResponsePreview
          responseId={previewResponseId}
          vendorId={vendor._id}
          onClose={() => setPreviewResponseId(null)}
        />
      )}
      {questionnaire && catalog && (isCreating || isDuplicating) && (
        <QuestionnaireWizard
          open={wizardOpen}
          onClose={handleWizardClose}
          onSave={handleWizardSave}
          questionnaire={questionnaire}
          catalog={catalog}
          isProcessor
          cutsheetType="vendor"
          initialResponse={initialResponse}
        />
      )}
    </Container>
  );
}
